import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from '../config/axios';
import { getApiUrl } from '../config/environment';
import { searchNavigationCatalog, type NavCatalogEntry } from '../navigation/appNavigationCatalog';

export type FinderPatientOption = {
  id: string;
  full_name: string;
  cpf?: string | null;
};

export type SystemFinderFlatRow =
  | { kind: 'patient'; key: string; patient: FinderPatientOption }
  | { kind: 'nav'; key: string; entry: NavCatalogEntry };

export function useSystemFinderSearch(
  navigationEntries: NavCatalogEntry[],
  enabled: boolean,
) {
  const [query, setQuery] = useState('');
  const [patients, setPatients] = useState<FinderPatientOption[]>([]);
  const [patientLoading, setPatientLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const patientSeqRef = useRef(0);
  const patientTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const navResults = useMemo(
    () => searchNavigationCatalog(query, navigationEntries, 15),
    [navigationEntries, query],
  );

  useEffect(() => {
    if (!enabled) return undefined;
    if (patientTimerRef.current) clearTimeout(patientTimerRef.current);
    const term = query.trim();
    const delay = term ? 280 : 0;
    patientTimerRef.current = setTimeout(async () => {
      const seq = ++patientSeqRef.current;
      setPatientLoading(true);
      try {
        const params: Record<string, string | number> = { limit: 10 };
        if (term) params.search = term;
        const { data } = await axios.get(getApiUrl('/clinic/patients'), { params });
        if (seq !== patientSeqRef.current) return;
        const rows = Array.isArray(data?.data) ? data.data : [];
        setPatients(rows.map((p: any) => ({
          id: String(p.id),
          full_name: String(p.full_name || p.social_name || 'Paciente'),
          cpf: p.cpf,
        })));
      } catch {
        if (seq === patientSeqRef.current) setPatients([]);
      } finally {
        if (seq === patientSeqRef.current) setPatientLoading(false);
      }
    }, delay);
    return () => {
      if (patientTimerRef.current) clearTimeout(patientTimerRef.current);
    };
  }, [enabled, query]);

  const flatRows: SystemFinderFlatRow[] = useMemo(() => {
    const rows: SystemFinderFlatRow[] = [];
    for (const patient of patients.slice(0, 10)) {
      rows.push({ kind: 'patient', key: `patient:${patient.id}`, patient });
    }
    for (const entry of navResults) {
      rows.push({ kind: 'nav', key: `nav:${entry.path}`, entry });
    }
    return rows;
  }, [navResults, patients]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, flatRows.length]);

  const reset = useCallback(() => {
    setQuery('');
    setPatients([]);
    setActiveIndex(0);
  }, []);

  return {
    query,
    setQuery,
    patientLoading,
    patients,
    flatRows,
    activeIndex,
    setActiveIndex,
    reset,
  };
}
